import { useMemo } from 'react';
import type {
  EntityListTableColumns,
  DynamicEntityListTableColumn,
  EntityListTableColumnHeader,
} from '@web-config-app/core';
import type { EntityListTableSelectionMode } from '../../types/components.types';
import {
  defaultColumnKeys,
  defaultSelectableColumnKeys,
  defaultMultiSelectableColumnKeys,
  isDefaultColumnKey,
  isDefaultOrSelectableColumnKey,
} from './cell-renderers/renderer-registry';
import type {
  DefaultColumnRendererKey,
  SelectableColumnRendererKey,
} from './cell-renderers/renderer-registry';
import { isDynamicColumn } from './is-dynamic-column';

export interface UseEntityListTableHeadersProps {
  columns?: EntityListTableColumns;
  selectionMode: EntityListTableSelectionMode;
}

const columnKeysBySelectableMode: Record<
  EntityListTableSelectionMode,
  Array<DefaultColumnRendererKey | SelectableColumnRendererKey>
> = {
  single: defaultSelectableColumnKeys,
  multi: defaultMultiSelectableColumnKeys,
  none: defaultColumnKeys,
};

const defaultColumnHeaderProperties: Record<
  DefaultColumnRendererKey,
  EntityListTableColumnHeader
> = {
  name: {
    id: 'name',
    label: { key: 'NAME' },
  },
  id: {
    id: 'id',
    label: { key: 'ID' },
    width: '20%',
  },
  date: {
    id: 'date',
    label: { key: 'LAST_UPDATED' },
    width: '15%',
  },
  status: {
    id: 'status',
    label: { key: 'STATUS' },
    width: '15%',
  },
};

const getColumnKeys = (
  columns: EntityListTableColumns | undefined,
  selectableMode: EntityListTableSelectionMode,
) => {
  if (columns && columns.length > 0) {
    const columnKeys = columns.map((column) => column.id);
    switch (selectableMode) {
      case 'single':
        return ['radioIndicator', ...columnKeys];
      case 'multi':
        return ['checkbox', ...columnKeys];
      default:
        return columnKeys;
    }
  }
  return columnKeysBySelectableMode[selectableMode];
};

export type DefaultDataColumn = {
  key: DefaultColumnRendererKey;
};

export const useEntityListTableColumns = ({
  selectionMode = 'none',
  columns,
}: UseEntityListTableHeadersProps): {
  headerColumns: Array<EntityListTableColumnHeader>;
  dataColumns: Array<DefaultDataColumn | DynamicEntityListTableColumn>;
} => {
  const columnKeys = getColumnKeys(columns, selectionMode);

  const headerColumns = useMemo(
    () =>
      columnKeys
        .map((key) => {
          const header = isDefaultColumnKey(key)
            ? defaultColumnHeaderProperties[key]
            : columns?.find((c) => c.id === key);

          return header;
        })
        .filter(
          (header): header is EntityListTableColumnHeader =>
            header !== undefined,
        ),
    [columnKeys, columns],
  );

  const dataColumns = useMemo(
    () =>
      columnKeys
        .map((key) => {
          if (isDefaultOrSelectableColumnKey(key)) {
            return { key };
          }
          const dataColumn = columns?.find((c) => c.id === key);

          return isDynamicColumn(dataColumn) ? dataColumn : undefined;
        })
        .filter(
          (col): col is DefaultDataColumn | DynamicEntityListTableColumn =>
            col !== undefined,
        ),
    [columnKeys, columns],
  );

  return {
    headerColumns,
    dataColumns,
  };
};
